// @ts-nocheck
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';

import API_ENDPOINTS from '../const/ApiEndPoints';
import { AI_AUTOFILL_TOAST_STATUS, EMPTY_OBJECT } from '../const/CommonConst';
import swalToast from '../components/Common/swalToast';
import { getVisibleAttributes } from '../utils/conditionalRuleEvaluator';

/**
 * Suggestion status enum
 */
const SUGGESTION_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
};

/**
 * Extracts all attributes from template form details (basic + section attributes)
 * @param {Object} formDetails - Template form details
 * @returns {Array} All attributes with section info
 */
const extractAllAttributesWithSections = (formDetails) => {
  const basicAttributes = (formDetails?.attributes || []).map((attr) => ({
    ...attr,
    _sectionName: 'Preliminary Details',
  }));
  const sectionAttributes =
    formDetails?.sections?.reduce((acc, section) => {
      const attrs = (section?.attributes || []).map((attr) => ({
        ...attr,
        _sectionName: section?.section_name,
      }));
      return [...acc, ...attrs];
    }, []) || [];
  return [...basicAttributes, ...sectionAttributes];
};

/**
 * Custom hook for managing AI autofill feature
 * Handles document upload, suggestion management (accept/reject/bulk), and form integration
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.formDetails - Template form details with sections and attributes
 * @param {Object} params.attributeDetails - Current attribute values
 * @param {Object} params.entityDetailsFormikForm - Formik form instance
 * @param {Function} params.handleAutoSave - Auto-save handler for draft
 * @param {string} params.entityType - Entity type for suggest-fields API
 * @param {string|number} params.entityId - Entity id for suggest-fields API
 * @param {string|number} params.templateId - Template id for suggest-fields API
 * @param {Function} params.onViewSuggestions - Callback when user clicks "View Suggestions" on toast
 * @returns {Object} Hook state and handlers
 */
const useAIAutofill = ({
  formDetails,
  attributeDetails = EMPTY_OBJECT,
  entityDetailsFormikForm,
  handleAutoSave,
  entityType,
  entityId,
  templateId,
  onViewSuggestions,
}) => {
  const [suggestions, setSuggestions] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [isBackgroundProcessing, setIsBackgroundProcessing] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const suggestionsRef = useRef({});
  const abortControllerRef = useRef(null);
  const isBackgroundProcessingRef = useRef(false);

  const KEY = 'ai-autofill-processing';
  // Abort any in-flight request and clear the background toast when the
  // consuming component unmounts (e.g., user navigates away from the model).
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      isBackgroundProcessingRef.current = false;
      globalThis._activeAIAutofillToast = null;
      globalThis._aiAutofillToastCallbacks = null;
      globalThis.closeSnackbar?.(KEY);
    };
  }, []);

  // Keep ref in sync
  const updateSuggestions = useCallback((newSuggestions) => {
    setSuggestions(newSuggestions);
    suggestionsRef.current = newSuggestions;
  }, []);

  // Build attribute metadata lookup
  const attributeMetadataMap = useMemo(() => {
    const allAttrs = extractAllAttributesWithSections(formDetails);
    const map = {};
    allAttrs.forEach((attr) => {
      map[attr?.id] = {
        label: attr?.label || attr?.name,
        sectionName: attr?._sectionName || 'Unknown Section',
        dataType: attr?.data_type,
        inputControlType: attr?.input_control_type,
        dropDownOptions: attr?.drop_down_options,
        isEditable: attr?.is_editable,
        isDerived: Boolean(attr?.is_derived),
      };
    });
    return map;
  }, [formDetails]);

  /**
   * Opens the upload modal
   */
  const openUploadModal = useCallback(() => {
    setIsUploadModalOpen(true);
  }, []);

  /**
   * Closes the upload modal
   */
  const closeUploadModal = useCallback(() => {
    setIsUploadModalOpen(false);
  }, []);

  /**
   * Opens the review drawer
   */
  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  /**
   * Closes the review drawer
   */
  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  /**
   * Submits a document for AI suggestion generation via sync POST.
   *
   * Request lifecycle:
   * 1) Marks processing state and stores uploaded filename for modal UI.
   * 2) Cancels any previous in-flight request to avoid race conditions where
   *    stale responses overwrite newer suggestions.
   * 3) Sends multipart/form-data payload with file + entity/template context.
   * 4) Validates API response and enriches raw suggestions with local metadata
   *    required by the review drawer (section name, label, data type, etc.).
   * 5) Filters out invalid suggestions:
   *    - unknown attributes
   *    - currently hidden attributes (conditional visibility)
   *    - non-editable or derived attributes
   *    - suggestions with non-positive numeric confidence
   * 6) Persists suggestion state, opens review drawer, and shows feedback toast.
   *
   * Side effects:
   * - Mutates UI state for upload modal/drawer and background-processing flag.
   * - Cancels active network request when a new submit starts.
   * - Emits success/error toasts for user feedback.
   *
   * @param {File} file - The uploaded document file
   */
  const submitForAISuggestions = useCallback(
    async (file) => {
      // Start foreground processing and reflect selected file in the modal.
      setIsProcessing(true);
      setUploadedFileName(file?.name || '');

      // Ensure only one active request exists at a time.
      // This prevents out-of-order responses from stale requests.
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        // Build request payload with file and server-side context required to
        // generate attribute suggestions for the current entity/template.
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', entityType || '');
        formData.append('template_id', templateId || '');
        formData.append('entity_id', entityId || '');

        const response = await axios.post(
          `${API_ENDPOINTS?.MODULE_BASE_URL?.AUTH}${API_ENDPOINTS?.LLM?.SUGGEST_FIELDS}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal: controller.signal,
            suppressErrorToast: true,
            loader: false,
          }
        );

        const data = response?.data;

        // API can return a successful HTTP status but logical failure payload.
        if (data?.status === 'failed') {
          throw new Error(data?.error || 'AI analysis failed.');
        }

        // Convert API suggestions into review-ready objects enriched with local
        // attribute metadata and current form values.
        const apiSuggestions = data?.suggestions || {};
        const enrichedSuggestions = {};
        const formValues = entityDetailsFormikForm?.values || {};
        const allAttributes = extractAllAttributesWithSections(formDetails);
        // Hidden attributes are excluded to avoid suggesting values for fields
        // the user cannot currently see/edit due to conditional rules.
        const { hiddenAttributes } = getVisibleAttributes(
          allAttributes,
          formValues
        );
        const hiddenAttrIds = new Set(
          (hiddenAttributes || []).map((attribute) => String(attribute?.id))
        );

        Object.keys(apiSuggestions).forEach((attrId) => {
          const meta = attributeMetadataMap[attrId];
          // Ignore unknown attributes not present in template metadata.
          if (!meta) {
            return;
          }

          // Ignore suggestions for conditionally hidden attributes.
          if (hiddenAttrIds.has(String(attrId))) {
            return;
          }

          // Ignore non-editable/derived attributes to prevent invalid updates.
          if (meta?.isEditable === false || meta?.isDerived) {
            return;
          }

          // Confidence can arrive as string/number. Only exclude finite values
          // that are explicitly <= 0; keep missing/non-numeric confidences.
          const rawConfidence = apiSuggestions[attrId]?.confidence;
          const numericConfidence = Number(rawConfidence);
          if (Number.isFinite(numericConfidence) && numericConfidence <= 0) {
            return;
          }

          // Store previous value for undo and suggestion comparison UX.
          const oldValue =
            formValues?.[attrId] ?? attributeDetails?.[attrId] ?? null;

          enrichedSuggestions[attrId] = {
            oldValue,
            newValue: apiSuggestions[attrId]?.value,
            confidence: apiSuggestions[attrId]?.confidence,
            sourceExcerpt: apiSuggestions[attrId]?.source_excerpt || null,
            status: SUGGESTION_STATUS.PENDING,
            attributeLabel: meta?.label,
            sectionName: meta?.sectionName,
            dataType: meta?.dataType,
            inputControlType: meta?.inputControlType,
          };
        });

        // Persist generated suggestions and transition from upload modal to
        // review drawer where users can accept/reject individual suggestions.
        updateSuggestions(enrichedSuggestions);
        setIsUploadModalOpen(false);

        const suggestionCount = Object.keys(enrichedSuggestions).length;

        // When running in background, update the toast in-place instead of
        // opening the drawer or showing a separate swalToast.
        if (isBackgroundProcessingRef.current) {
          globalThis._aiAutofillToastCallbacks?.onComplete?.(suggestionCount);
        } else {
          setIsDrawerOpen(true);
          swalToast({
            icon: 'success',
            title: `AI generated ${suggestionCount} suggestion${suggestionCount === 1 ? '' : 's'} for your review.`,
            id: 'ai-autofill-success',
          });
        }
      } catch (error) {
        // Aborts are expected during cancel/retry/navigate-away flows; no
        // user-facing error. The global axios interceptor rewraps the original
        // error with `new Error(error)`, so `name` becomes "Error" and the
        // cancellation marker only survives in `message` ("CanceledError:
        // canceled"). Check name, code, and message to catch all shapes.
        const isCancelled =
          error?.name === 'CanceledError' ||
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED' ||
          /cancell?ed/i.test(error?.message || '');
        if (isCancelled) {
          return;
        }
        console.error('AI Autofill error:', error);

        // When running in background, update the toast to failed state
        if (isBackgroundProcessingRef.current) {
          const errorMsg =
            error?.response?.data?.msg ||
            error?.message ||
            'Failed to generate AI suggestions. Please try again.';
          globalThis._aiAutofillToastCallbacks?.onFail?.(errorMsg);
        } else {
          swalToast({
            icon: 'error',
            title:
              error?.response?.data?.msg ||
              error?.message ||
              'Failed to generate AI suggestions. Please try again.',
            id: 'ai-autofill-error',
          });
        }
      } finally {
        // Always release foreground/background loading state and clear the
        // request ref so future submissions start with a clean lifecycle.
        setIsProcessing(false);
        setIsBackgroundProcessing(false);
        isBackgroundProcessingRef.current = false;
        abortControllerRef.current = null;
      }
    },
    [
      entityType,
      entityId,
      templateId,
      attributeDetails,
      entityDetailsFormikForm,
      attributeMetadataMap,
      updateSuggestions,
    ]
  );

  /**
   * Cancels the in-flight AI suggestion request
   */
  const cancelProcessing = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsProcessing(false);
    setIsBackgroundProcessing(false);
    isBackgroundProcessingRef.current = false;
    // Dismiss any active toast
    globalThis.closeSnackbar?.(KEY);
  }, []);

  /**
   * Moves processing to background (modal closed, request continues).
   * Enqueues a persistent toast to show processing status.
   */
  const continueInBackground = useCallback(() => {
    setIsBackgroundProcessing(true);
    isBackgroundProcessingRef.current = true;
    setIsUploadModalOpen(false);

    // Prevent duplicate toasts
    if (globalThis._activeAIAutofillToast) {
      return;
    }

    if (globalThis.enqueueSnackbar) {
      globalThis.enqueueSnackbar(
        JSON.stringify({
          fileName: uploadedFileName || 'Document',
          status: AI_AUTOFILL_TOAST_STATUS.IN_PROGRESS,
        }),
        {
          variant: 'aiAutofill',
          key: KEY,
          persist: true,
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          onViewSuggestions,
          onRetry: openUploadModal,
        }
      );
      globalThis._activeAIAutofillToast = KEY;
    }
  }, [onViewSuggestions, openUploadModal, uploadedFileName]);

  /**
   * Accepts a single suggestion — sets the suggested value in the form
   * @param {string|number} attrId - Attribute ID
   */
  const acceptSuggestion = useCallback(
    (attrId) => {
      const suggestion = suggestionsRef.current?.[attrId];
      if (!suggestion || suggestion?.status !== SUGGESTION_STATUS.PENDING) {
        return;
      }

      // Set form value
      entityDetailsFormikForm?.setFieldValue(
        String(attrId),
        suggestion?.newValue
      );

      // Trigger auto-save for draft
      if (handleAutoSave) {
        handleAutoSave(String(attrId), suggestion?.newValue);
      }

      // Update suggestion status
      const updated = {
        ...suggestionsRef.current,
        [attrId]: {
          ...suggestion,
          status: SUGGESTION_STATUS.ACCEPTED,
        },
      };
      updateSuggestions(updated);
    },
    [entityDetailsFormikForm, handleAutoSave, updateSuggestions]
  );

  /**
   * Rejects a single suggestion — no form change
   * @param {string|number} attrId - Attribute ID
   */
  const rejectSuggestion = useCallback(
    (attrId) => {
      const suggestion = suggestionsRef.current?.[attrId];
      if (!suggestion || suggestion?.status !== SUGGESTION_STATUS.PENDING) {
        return;
      }

      const updated = {
        ...suggestionsRef.current,
        [attrId]: {
          ...suggestion,
          status: SUGGESTION_STATUS.REJECTED,
        },
      };
      updateSuggestions(updated);
    },
    [updateSuggestions]
  );

  /**
   * Undoes an accepted suggestion — reverts form value to oldValue and sets status back to pending
   * @param {string|number} attrId - Attribute ID
   */
  const undoSuggestion = useCallback(
    (attrId) => {
      const suggestion = suggestionsRef.current?.[attrId];
      if (!suggestion || suggestion?.status !== SUGGESTION_STATUS.ACCEPTED) {
        return;
      }

      // Revert form value to original
      entityDetailsFormikForm?.setFieldValue(
        String(attrId),
        suggestion?.oldValue
      );

      if (handleAutoSave) {
        handleAutoSave(String(attrId), suggestion?.oldValue);
      }

      // Set status back to pending
      const updated = {
        ...suggestionsRef.current,
        [attrId]: {
          ...suggestion,
          status: SUGGESTION_STATUS.PENDING,
        },
      };
      updateSuggestions(updated);
    },
    [entityDetailsFormikForm, handleAutoSave, updateSuggestions]
  );

  /**
   * Accepts all pending suggestions
   */
  const acceptAll = useCallback(() => {
    const current = { ...suggestionsRef.current };
    const pendingIds = Object.keys(current).filter(
      (id) => current[id]?.status === SUGGESTION_STATUS.PENDING
    );

    pendingIds.forEach((attrId) => {
      const suggestion = current[attrId];
      entityDetailsFormikForm?.setFieldValue(
        String(attrId),
        suggestion?.newValue
      );
      if (handleAutoSave) {
        handleAutoSave(String(attrId), suggestion?.newValue);
      }
      current[attrId] = {
        ...suggestion,
        status: SUGGESTION_STATUS.ACCEPTED,
      };
    });

    updateSuggestions(current);

    swalToast({
      icon: 'success',
      title: `Accepted ${pendingIds.length} suggestion${pendingIds.length === 1 ? '' : 's'}.`,
      id: 'ai-autofill-accept-all',
    });
  }, [entityDetailsFormikForm, handleAutoSave, updateSuggestions]);

  /**
   * Rejects all pending suggestions
   */
  const rejectAll = useCallback(() => {
    const current = { ...suggestionsRef.current };
    const pendingIds = Object.keys(current).filter(
      (id) => current[id]?.status === SUGGESTION_STATUS.PENDING
    );

    pendingIds.forEach((attrId) => {
      current[attrId] = {
        ...current[attrId],
        status: SUGGESTION_STATUS.REJECTED,
      };
    });

    updateSuggestions(current);

    swalToast({
      icon: 'info',
      title: `Rejected ${pendingIds.length} suggestion${pendingIds.length === 1 ? '' : 's'}.`,
      id: 'ai-autofill-reject-all',
    });
  }, [updateSuggestions]);

  /**
   * Undoes all accepted suggestions — reverts form values to oldValues and sets status back to pending
   */
  const undoAll = useCallback(() => {
    const current = { ...suggestionsRef.current };
    const acceptedIds = Object.keys(current).filter(
      (id) => current[id]?.status === SUGGESTION_STATUS.ACCEPTED
    );

    acceptedIds.forEach((attrId) => {
      const suggestion = current[attrId];
      entityDetailsFormikForm?.setFieldValue(
        String(attrId),
        suggestion?.oldValue
      );
      if (handleAutoSave) {
        handleAutoSave(String(attrId), suggestion?.oldValue);
      }
      current[attrId] = {
        ...suggestion,
        status: SUGGESTION_STATUS.PENDING,
      };
    });

    updateSuggestions(current);

    swalToast({
      icon: 'info',
      title: `Reverted ${acceptedIds.length} suggestion${acceptedIds.length === 1 ? '' : 's'}.`,
      id: 'ai-autofill-undo-all',
    });
  }, [entityDetailsFormikForm, handleAutoSave, updateSuggestions]);

  /**
   * Clears all suggestions — used when exiting edit mode.
   * Also dismisses background toast and cancels in-flight requests.
   */
  const clearSuggestions = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    updateSuggestions({});
    setIsDrawerOpen(false);
    setIsUploadModalOpen(false);
    setUploadedFileName('');
    setIsProcessing(false);
    setIsBackgroundProcessing(false);
    isBackgroundProcessingRef.current = false;
    // Dismiss any active background toast
    globalThis.closeSnackbar?.(KEY);
  }, [updateSuggestions]);

  /**
   * Gets the suggestion for a specific attribute (used by field components)
   * @param {string|number} attrId - Attribute ID
   * @returns {Object|null} The suggestion object or null
   */
  const getSuggestionForAttribute = useCallback(
    (attrId) => {
      return suggestions?.[attrId] || null;
    },
    [suggestions]
  );

  // Computed stats
  const suggestionStats = useMemo(() => {
    const all = Object.values(suggestions);
    return {
      total: all.length,
      pending: all.filter((s) => s?.status === SUGGESTION_STATUS.PENDING)
        .length,
      accepted: all.filter((s) => s?.status === SUGGESTION_STATUS.ACCEPTED)
        .length,
      rejected: all.filter((s) => s?.status === SUGGESTION_STATUS.REJECTED)
        .length,
    };
  }, [suggestions]);

  // Whether there are any suggestions at all
  const hasSuggestions = useMemo(() => {
    return Object.keys(suggestions).length > 0;
  }, [suggestions]);

  // Whether there are pending suggestions
  const hasPendingSuggestions = useMemo(() => {
    return suggestionStats.pending > 0;
  }, [suggestionStats]);

  return {
    // State
    suggestions,
    isProcessing,
    isBackgroundProcessing,
    isDrawerOpen,
    isUploadModalOpen,
    uploadedFileName,
    hasSuggestions,
    hasPendingSuggestions,
    suggestionStats,

    // Modal/Drawer controls
    openUploadModal,
    closeUploadModal,
    openDrawer,
    closeDrawer,

    // Actions
    submitForAISuggestions,
    cancelProcessing,
    continueInBackground,
    acceptSuggestion,
    rejectSuggestion,
    undoSuggestion,
    acceptAll,
    rejectAll,
    undoAll,
    clearSuggestions,
    getSuggestionForAttribute,
  };
};

export { SUGGESTION_STATUS };
export default useAIAutofill;
